import { addRecord, getRecordByIndex, STORES, updateRecord } from './indexedDBUtils.js'

let saveQueue = Promise.resolve()

export const PVI_ENCOUNTER_MODES = Object.freeze({
  PWA: 'pwa',
  FLEXI_TEXT: 'flexi_text',
  FLEXI_GUIDED: 'flexi_guided',
})

/**
 * Saves patient visit & insight data to IndexedDB with upsert logic.
 *
 * The function first maps the provided arguments into a single record via
 * {@link mappingPatientVisitInsights}, then performs an upsert against the
 * `patientVisitInsights` store keyed on `encounterId`:
 * - If a record with the same `encounterId` already exists, it is merged with
 *   the new data while preserving the existing primary key (`id`).
 * - If no matching record is found, a new record is inserted.
 *
 * This prevents duplicate entries for the same encounter across multiple calls
 * (e.g., when the user revisits the triage results screen).
 *
 * @param {object} input - Visit data captured when an encounter is completed.
 * @param {Array<object>} [input.conditions] - Final MRX assessments.
 * @param {Array<object>} [input.categoriesData] - MRX condition categories.
 * @param {object} [input.currentTrack] - Active patient track.
 * @param {Function|object} [input.sessionDetails] - Captured session snapshot,
 *   provided either as a getter function or as the snapshot object. May be
 *   omitted when `input.encounterId` is provided.
 * @param {string|null} [input.selectedTriage] - Selected or overall triage.
 * @param {Array<string>} [input.selectedConditions] - User-selected conditions.
 * @param {string} [input.encounterId] - Explicit exported encounter identifier.
 * @param {'pwa'|'flexi_text'|'flexi_guided'} [input.encounterMode='pwa'] - Encounter source.
 * @returns {Promise<IDBValidKey>} A promise that resolves to the IndexedDB record
 *   key of the upserted record (either the existing key on update, or the
 *   auto-generated key on insert).
 * @throws {Error} Throws if any IndexedDB operation fails.
 */
export async function savePatientVisitInsights({
  conditions,
  categoriesData,
  currentTrack,
  sessionDetails,
  selectedTriage,
  selectedConditions,
  encounterId,
  encounterMode = PVI_ENCOUNTER_MODES.PWA,
}) {
  const data = mappingPatientVisitInsights({
    conditions,
    categoriesData,
    currentTrack,
    sessionDetails,
    selectedTriage,
    selectedConditions,
    encounterId,
    encounterMode,
  })

  const executeDbOp = async () => {
    try {
      if (data?.encounterId) {
        const existing = await getRecordByIndex(
          STORES.PATIENT_VISIT_INSIGHTS,
          'encounterId',
          data.encounterId
        )
        if (existing) {
          const updated = { ...existing, ...data, id: existing.id }
          await updateRecord(STORES.PATIENT_VISIT_INSIGHTS, updated)
          return existing.id
        }
      }
      return await addRecord(STORES.PATIENT_VISIT_INSIGHTS, data)
    } catch (error) {
      console.error("Failed to save PatientVisitInsights to IndexedDB: ", error)
      throw error
    }
  }

  const promise = saveQueue.then(executeDbOp, executeDbOp)
  saveQueue = promise.catch(() => {})
  return promise
}

/**
 * Maps raw triage result data into a structured patient visit insight record
 * ready to be persisted in IndexedDB.
 *
 * The returned record contains session metadata, user identifier, condition
 * details with their associated categories, patient type, and the calculated
 * duration of the visit.
 *
 * The `duration` field is computed as the difference in seconds between the
 * session's `timing.start` and `timing.end` timestamps. If either timestamp
 * is missing, the value will be `null`.
 *
 * @param {object} input - Visit data to convert into a persisted PVI record.
 * @param {Array<object>} [input.conditions] - Final MRX assessments.
 * @param {Array<object>} [input.categoriesData] - MRX condition categories.
 * @param {object} [input.currentTrack] - Active patient track.
 * @param {Function|object} [input.sessionDetails] - Captured session snapshot,
 *   provided either as a getter function or as the snapshot object. May be
 *   omitted when `input.encounterId` is provided.
 * @param {string|null} [input.selectedTriage] - Selected or overall triage.
 * @param {Array<string>} [input.selectedConditions] - User-selected conditions.
 * @param {string} [input.encounterId] - Explicit exported encounter identifier.
 * @param {'pwa'|'flexi_text'|'flexi_guided'} [input.encounterMode='pwa'] - Encounter source.
 * @returns {object} A single mapped record object with the following fields:
 *   - `createdAt` (string): ISO timestamp of when the record was created
 *   - `encounterId` (string): explicit encounter ID or the session snapshot's `id.id`
 *   - `encounterMode` ('pwa'|'flexi_text'|'flexi_guided'): encounter source
 *   - `patientType` (string): patient type derived from `currentTrack.path`
 *   - `duration` (number|null): duration of the visit in seconds, or null if unavailable
 *   - `userId` (string|number): user identifier from the session snapshot's `user.id`, or `""`
 *   - `triage` (string|null): the selected triage value
 *   - `conditions` (Array<object>): list of condition records, each containing:
 *     - `name` (string): condition identifier
 *     - `severity` (string): severity level
 *     - `triage_recommendation` (string): recommended triage action
 *     - `categories` (Array): associated category objects, or `[]` if none found
 *     - `selectedByUser` (boolean): whether the condition was selected/toggled by the user
 */
export function mappingPatientVisitInsights({
  conditions,
  categoriesData,
  currentTrack,
  sessionDetails,
  selectedTriage,
  selectedConditions,
  encounterId,
  encounterMode = PVI_ENCOUNTER_MODES.PWA,
}) {
  const categoriesSet = extractCategories(categoriesData)

  const now = new Date()
  const timingData = sessionDetails()?.timing || {}
  const startTime = timingData?.start ? new Date(timingData?.start) : null
  const endTime = timingData?.end ? new Date(timingData?.end) : null
  const pausedDurationMs = timingData?.pausedDurationMs || 0
  const userDetails = sessionDetails()?.user || {}

  const timePerVisit = startTime && endTime
    ? Math.floor((endTime - startTime - pausedDurationMs) / 1000)
    : null

  const selectedSet = new Set(selectedConditions || [])

  return {
    createdAt: now.toISOString(),
    encounterId: encounterId || sessionDetails()?.id?.id,
    encounterMode,
    patientType: currentTrack?.path,
    duration: timePerVisit,
    userId: userDetails?.id || "",
    deviceId: userDetails?.hr_id || "",
    triage: selectedTriage,
    conditions: conditions.map(condition => {
      return {
        name: condition.name,
        severity: condition.severity,
        triage_recommendation: condition.triage_recommendation,
        categories: categoriesSet[condition.name] || [],
        selectedByUser: selectedSet.has(condition.name),
      }
    })
  }

}

/**
 * Builds a condition-to-category lookup map from the raw categories data array.
 *
 * Iterates over each condition entry in `categoriesData` and constructs an object
 * where the key is the condition's `title` (if available) or `name` (as fallback),
 * and the value is the array of categories associated with that condition.
 *
 * Conditions with an empty or missing `categories` array are skipped and will not
 * appear in the resulting map.
 *
 * This map is used by `mappingPatientVisitInsights` to efficiently look up the
 * category data for each diagnosed condition by name.
 *
 * @param {Array<object>} categoriesData - Array of condition objects to process.
 *   Each object should have:
 *   - `name` (string): the condition's unique key, used as fallback lookup key
 *   - `title` (string, optional): preferred display key; used over `name` if present
 *   - `categories` (Array): list of category items associated with the condition;
 *     entries with an empty array are excluded from the result
 * @returns {object} A flat key-value map where:
 *   - key: condition title or name (string)
 *   - value: array of category objects for that condition
 *
 * @example
 * const data = [
 *   { name: 'malaria', title: 'Malaria', categories: [{ label: 'Fever' }] },
 *   { name: 'anemia', title: '', categories: [] }, // skipped - empty categories
 * ]
 * extractCategories(data)
 * // => { 'Malaria': [{ label: 'Fever' }] }
 */
const extractCategories = (categoriesData) => {
  const result = {}

  categoriesData.forEach(condition => {
    const diseaseKey = condition.name

    if (condition.categories && condition.categories.length > 0) {
      result[condition.title || diseaseKey] = condition.categories
    }
  })

  return result;
}